import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import {
  LEAVE_ATTACHMENT_ACCEPT,
  LEAVE_ATTACHMENT_MAX_FILES,
  LEAVE_ATTACHMENT_MAX_MB,
  SupportingDocumentRule,
  type LeaveRequestAttachment,
} from '@gokrew/models/leave-management';
import { DropzoneField } from '@/form/DropzoneField';
import { usePreviewLeaveAttachment } from './usePreviewLeaveAttachment';
import { formatFileSize } from '@/utility/utility';
import styles from './LeaveAttachments.module.css';

const ns = TranslationNS.LeaveManagement;
const key = (leaf: string) => `leave_attachments.${leaf}`;

/**
 * The supporting documents ("justificatifs") of a leave request, shared by
 * both portals: each opens in the in-app preview (with Download). Renders
 * nothing when there is none.
 */
export function LeaveAttachmentList({
  attachments,
}: {
  attachments: LeaveRequestAttachment[] | null | undefined;
}) {
  const { t } = useTranslation(ns);
  const openPreview = usePreviewLeaveAttachment();
  if (!attachments?.length) return null;
  return (
    <section className={styles.section} aria-label={t(key('title'))}>
      <span className={styles.label}>{t(key('title'))}</span>
      <ul className={styles.list}>
        {attachments.map((file) => (
          <li key={file.id} className={styles.item}>
            <FileText className={styles.icon} aria-hidden="true" />
            <button
              type="button"
              className={styles.name}
              title={file.fileName}
              disabled={!file.url}
              onClick={() =>
                file.url &&
                openPreview({
                  url: file.url,
                  fileName: file.fileName,
                  mimeType: file.mimeType,
                })
              }
            >
              {file.fileName}
            </button>
            <span className={styles.size}>
              {formatFileSize(file.sizeBytes)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The attach area of a leave request form (Formik, a `File[]` field), shown
 * per the leave type's supporting document rule: nothing for `none`, an
 * optional or required dropzone otherwise (3 files, PDF / JPEG / PNG /
 * HEIC, 10 MB). The form uploads with `uploadLeaveAttachments` on submit.
 */
export function LeaveAttachmentsField({
  name,
  rule,
  disabled,
}: {
  name: string;
  rule: SupportingDocumentRule | null | undefined;
  disabled?: boolean;
}) {
  const { t } = useTranslation(ns);
  if (rule === SupportingDocumentRule.None) return null;
  const required = rule === SupportingDocumentRule.Required;
  return (
    <div className={styles.section}>
      <DropzoneField
        name={name}
        label={t(key(required ? 'field_required' : 'field_optional'))}
        accept={LEAVE_ATTACHMENT_ACCEPT}
        acceptLabel={t(key('accept'), {
          count: LEAVE_ATTACHMENT_MAX_FILES,
          size: LEAVE_ATTACHMENT_MAX_MB,
        })}
        maxSizeMB={LEAVE_ATTACHMENT_MAX_MB}
        multiple
        max={LEAVE_ATTACHMENT_MAX_FILES}
        disabled={disabled}
      />
      {required && <p className={styles.hint}>{t(key('required_hint'))}</p>}
    </div>
  );
}
